import { previewMedia, studioDressColorways } from "$lib/content/media";
import { mediaUrl } from "$lib/utils/media";
import type {
  ClientLocationMarker,
  FaqItem,
  ServiceItem,
  ServiceShowcase,
  TestimonialItem,
  WorkGalleryItem,
  ShowcaseProjectItem,
} from "$lib/types/content";
import type { RouteId } from "$app/types";

export const navigationItems = [
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Pricing", href: "/pricing" },
  { label: "Portfolio", href: "/portfolio" },
  { label: "Events", href: "/events" },
  { label: "CSR", href: "/csr" },
  { label: "Careers", href: "/careers" },
  { label: "Contact", href: "/contact" },
] as const satisfies readonly { label: string; href: RouteId }[];

export const services: ServiceItem[] = [
  // Image Editing
  {
    title: "AI photo retouching",
    slug: "ai-retouch",
    description:
      "AI-assisted product and fashion photo retouching with human review.",
    media: {
      src: "/images/ai-retouching/ai-retouching-intro-coordinated-fashion-lookbook.webp",
      alt: "AI-styled model presenting a coordinated beige outfit with matching apparel and accessories",
      width: 1122,
      height: 1402,
      credit: "Studio Click House",
    },
    category: "Image Editing",
  },
  {
    title: "Background removal",
    slug: "background-remove",
    description: "Product background removal with clean edges and alpha masking.",
    media: {
      src: "/images/background-remove/background-remove-hero-black-trucker-hat.webp",
      alt: "Light wood Scandinavian round stool isolated against a clean background",
      width: 456,
      height: 570,
      credit: "Studio Click House",
    },
    category: "Image Editing",
  },
  {
    title: "Clipping path",
    slug: "clipping-path",
    description:
      "Hand-drawn clipping paths and multi-path isolation for product images.",
    media: {
      src: "/images/clipping-path/clipping-path-intro-multi-product-pen-path-examples.webp",
      alt: "Pen-tool path examples around handbags and footwear, including close-ups of leather and stitching",
      width: 1122,
      height: 1402,
      credit: "Studio Click House",
    },
    category: "Image Editing",
  },
  {
    title: "Ghost mannequin",
    slug: "ghost-mannequin",
    description:
      "Invisible mannequin editing and neck-joint compositing for apparel catalogs.",
    media: {
      src: "/images/ghost-mannequin/ghost-mannequin-hero-couture-ruffle-evening-gown.webp",
      alt: "Black couture evening gown with sculptural ruffle detailing on a ghost mannequin",
      width: 1600,
      height: 2000,
      credit: "Studio Click House",
    },
    category: "Image Editing",
  },
  {
    title: "Editorial retouching",
    slug: "editorial-retouching",
    description:
      "Fashion, beauty, and portrait retouching that preserves natural detail.",
    media: {
      src: "/images/editorial-retouching/editorial-retouching-intro-fashion-detail-workflow.webp",
      alt: "Editorial retouching detail board comparing skin, hair, garment, and footwear refinements",
      width: 1122,
      height: 1402,
      credit: "Studio Click House",
    },
    category: "Image Editing",
  },
  {
    title: "E-commerce photo retouching",
    slug: "ecommerce-retouching",
    description:
      "Product photo cleanup, consistent crops, and color for online catalogs.",
    media: {
      src: "/images/ecommerce-retouching/ecommerce-retouching-hero-skincare-serum-zen-stones.webp",
      alt: "Misolo skincare serum, cream, and dropper bottles styled with zen stones",
      width: 1600,
      height: 2000,
      credit: "Studio Click House",
    },
    category: "Image Editing",
  },
  {
    title: "Jewelry photo retouching",
    slug: "jewelry-retouching",
    description:
      "Macro jewelry retouching for metal, gemstones, and reflections.",
    media: {
      src: "/images/jewelry-retouching/jewelry-retouching-intro-gold-diamond-comparison-board.webp",
      alt: "Before and after jewelry retouching examples for diamond rings, a pendant, earrings, and bracelet",
      width: 1600,
      height: 2000,
      credit: "Studio Click House",
    },
    category: "Image Editing",
  },
  {
    title: "Product photo color correction",
    slug: "color-correction",
    description:
      "Product color matching and colorway consistency across catalog images.",
    media: {
      src: "/images/color-correction/color-correction-hero-designer-sunglasses-front-view.webp",
      alt: "Fashion model wearing a color-corrected and palette-matched top",
      width: 1600,
      height: 2000,
      credit: "Studio Click House",
    },
    category: "Image Editing",
  },

  // Video Editing
  {
    title: "AI video generation",
    slug: "ai-video-generation",
    description:
      "Generative motion synthesis, neural camera extensions, and cinematic AI sequences.",
    media: {
      src: "/images/video-editing/stage-1-raw-synthesis.webp",
      alt: "Neural AI motion synthesis and generative video pipeline",
      width: 1200,
      height: 670,
      credit: "Studio Click House",
    },
    category: "Video Editing",
  },
  {
    title: "Commercial editing",
    slug: "commercial-editing",
    description:
      "Polished post-production for brand reels, commercial spots, and product campaigns.",
    media: {
      src: "/images/portfolio/ai-video-editing.jpg",
      alt: "Professional commercial video editing suite with multi-track timeline and sequence grading",
      width: 1122,
      height: 1402,
      credit: "Studio Click House",
    },
    category: "Video Editing",
  },
  {
    title: "Color grading",
    slug: "color-grading",
    description:
      "Calibrated color balance and aesthetic toning for cohesive video sets.",
    media: {
      src: "/images/video-editing/stage-3-master-grade.webp",
      alt: "Cinematic color grading console with color wheels and waveform monitors",
      width: 1122,
      height: 1402,
      credit: "Studio Click House",
    },
    category: "Video Editing",
  },
  {
    title: "Social cutdowns",
    slug: "social-cutdowns",
    description:
      "Punchy, fast-turnaround video revisions tailored for social platforms.",
    media: {
      src: "/images/video-editing/commercial-hero-poster.webp",
      alt: "Fast-paced social video revision and motion upscale sequence",
      width: 1200,
      height: 670,
      credit: "Studio Click House",
    },
    category: "Video Editing",
  },
  // 3D Modeling
  {
    title: "3D product modeling",
    slug: "3d-product-modeling",
    description:
      "Accurate, lightweight 3D models built directly from reference designs.",
    media: {
      src: "/images/portfolio/3d-cgi-showcase-v2.webp",
      alt: "3D wireframe mesh modeling transitioning to finished product geometry",
      width: 1122,
      height: 1402,
      credit: "Studio Click House",
    },
    category: "3D Modeling",
  },
  {
    title: "Texturing & shading",
    slug: "texturing-shading",
    description:
      "Custom PBR material maps depicting authentic metal, glass, and fabrics.",
    media: {
      src: "/images/portfolio/3d-cgi-showcase-v2.webp",
      alt: "3D PBR material texturing, displacement maps, and realistic shader development",
      width: 1200,
      height: 675,
      credit: "Studio Click House",
    },
    category: "3D Modeling",
  },
  {
    title: "CGI rendering",
    slug: "cgi-rendering",
    description:
      "Photorealistic 3D product renders with custom studio staging.",
    media: {
      src: "/images/portfolio/graphical-fragrance-cgi.webp",
      alt: "Photorealistic 3D CGI product render of a luxury glass perfume bottle",
      width: 1024,
      height: 1024,
      credit: "Studio Click House",
    },
    category: "3D Modeling",
  },
];

export const serviceShowcases: ServiceShowcase[] = [
  {
    category: "Image Editing",
    displayTitle: "Image editing",
    description:
      "Isolation, retouching, color, and detail work for individual frames or complete production batches.",
    media: {
      kind: "comparison",
      beforeLabel: "Before",
      afterLabel: "After",
      before: {
        src: "/images/about/colorways/dress-color-original.jpg",
        alt: "Original studio portrait before color correction",
        width: 1024,
        height: 1536,
        credit: "Studio Click House local preview asset",
      },
      after: {
        ...studioDressColorways[0],
        credit: "Studio Click House local preview asset",
      },
    },
  },
  {
    category: "Video Editing",
    displayTitle: "Video editing",
    description:
      "Commercial edits, social cutdowns, and controlled grading shaped for each format and viewing context.",
    media: {
      kind: "video",
      src: "/videos/video-editing-ai-motion-synthesis.mp4",
      poster: previewMedia.editingWorkspace,
      alt: "Studio video editing and post-production preview",
    },
  },
  {
    category: "3D Modeling",
    displayTitle: "3D & CGI",
    description:
      "Product modeling, material development, and CGI rendering for believable commercial imagery.",
    media: {
      kind: "comparison",
      beforeLabel: "Model",
      afterLabel: "Render",
      before: previewMedia.product3dModel,
      after: previewMedia.perfumeStillLife,
    },
  },
];

export const workGalleryItems: WorkGalleryItem[] = [
  {
    id: "fashion-color",
    category: "Fashion color",
    title: "Color decisions that belong to the full story.",
    description:
      "Controlled color treatment helps separate the subject while keeping a campaign visually connected.",
    tags: ["Fashion", "Color"],
    media: previewMedia.redStudioPortrait,
  },
  {
    id: "jewelry-detail",
    category: "Jewelry detail",
    title: "Small surfaces, closely considered.",
    description:
      "Reflection, edge definition, and material detail are handled without flattening the object.",
    tags: ["Jewelry", "Detail"],
    media: previewMedia.jewelryMacro,
  },
  {
    id: "shadow-study",
    category: "Shadow study",
    title: "A grounded image still needs room to breathe.",
    description:
      "Directional shadow and negative space give a product weight without making the composition feel heavy.",
    tags: ["Shadow", "Composition"],
    media: previewMedia.perfumeShadow,
  },
];

export const testimonials: TestimonialItem[] = [
  {
    quote:
      "Great design is not just about how something looks; it’s about how it makes people see, feel, and remember.",
    name: "Jilhaj Hossain Raju",
    role: "Managing Director",
    company: "Studio Click House Ltd.",
    avatar: mediaUrl("/images/about/about-leadership-md-jilhaj.webp"),
  },
];

export const faqs: FaqItem[] = [
  {
    question: "Which file formats can you work with?",
    answer:
      "File requirements depend on the work. Share your available source files and intended outputs so the studio can confirm the right production path before work begins.",
  },
  {
    question: "How is turnaround planned?",
    answer:
      "Timing is scoped around image count, complexity, source quality, feedback stages, and final delivery needs. A schedule is confirmed for each project rather than assumed in advance.",
  },
  {
    question: "How do revisions work?",
    answer:
      "Revision needs are agreed during scoping. Clear visual references and consolidated feedback help the studio keep decisions consistent across the full image set.",
  },
  {
    question: "How should files be transferred?",
    answer:
      "The transfer method is confirmed with the project. Production files should be shared through an agreed secure workspace rather than public links containing sensitive material.",
  },
  {
    question: "Can you keep a large batch visually consistent?",
    answer:
      "Yes. Reference frames, shared finishing rules, and review checkpoints can be established before a larger batch moves through production.",
  },
  {
    question: "Can the studio join an existing creative workflow?",
    answer:
      "The studio can work from your naming, review, and delivery conventions when those requirements are shared during scoping.",
  },
];

export const clientLocations: ClientLocationMarker[] = [
  {
    id: "united-states",
    country: "United States",
    flag: "🇺🇸",
    clientCount: 1200,
    position: { lat: 39.8, lng: -98.6 },
  },
  {
    id: "brazil",
    country: "Brazil",
    flag: "🇧🇷",
    clientCount: 354,
    position: { lat: -14.2, lng: -51.9 },
  },
  {
    id: "argentina",
    country: "Argentina",
    flag: "🇦🇷",
    clientCount: 124,
    position: { lat: -38.4, lng: -63.6 },
  },
  {
    id: "south-africa",
    country: "South Africa",
    flag: "🇿🇦",
    clientCount: 68,
    position: { lat: -30.6, lng: 22.9 },
  },
  {
    id: "united-arab-emirates",
    country: "United Arab Emirates",
    flag: "🇦🇪",
    clientCount: 857,
    position: { lat: 23.4, lng: 53.8 },
  },
  {
    id: "bangladesh",
    country: "Bangladesh",
    flag: "🇧🇩",
    clientCount: 5000,
    position: { lat: 23.685, lng: 90.356 },
  },
  {
    id: "china",
    country: "China",
    flag: "🇨🇳",
    clientCount: 742,
    position: { lat: 35.86, lng: 104.2 },
  },
  {
    id: "australia",
    country: "Australia",
    flag: "🇦🇺",
    clientCount: 1050,
    position: { lat: -25.27, lng: 133.775 },
  },
  {
    id: "united-kingdom",
    country: "United Kingdom",
    flag: "🇬🇧",
    position: { lat: 54.8, lng: -3.4 },
  },
  {
    id: "denmark",
    country: "Denmark",
    flag: "🇩🇰",
    position: { lat: 56.26, lng: 9.5 },
  },
  {
    id: "norway",
    country: "Norway",
    flag: "🇳🇴",
    position: { lat: 60.47, lng: 8.47 },
  },
  {
    id: "spain",
    country: "Spain",
    flag: "🇪🇸",
    position: { lat: 40.46, lng: -3.75 },
  },
  {
    id: "france",
    country: "France",
    flag: "🇫🇷",
    position: { lat: 46.23, lng: 2.21 },
  },
  {
    id: "sweden",
    country: "Sweden",
    flag: "🇸🇪",
    position: { lat: 60.13, lng: 18.64 },
  },
];

export const showcaseProjects: ShowcaseProjectItem[] = [
  {
    id: "ai-image-workflow",
    year: "01",
    category: "AI image workflow",
    title: "AI photo editing",
    description:
      "AI-assisted fashion image production and finishing, with careful review of fabric texture and product detail.",
    capabilities: ["AI image workflow", "Art direction", "Detail finishing"],
    bgColor:
      "color-mix(in srgb, var(--color-brand-dark) 8%, var(--color-brand-paper))",
    media: {
      kind: "video",
      src: "/videos/home-fashion-ecommerce-montage.mp4",
      alt: "AI-assisted editorial fashion image and video post-production demonstration",
      width: 1920,
      height: 1080,
      credit: "Studio Click House",
      objectPosition: "top",
    },
    href: "/services",
  },
  {
    id: "photo-editing",
    year: "02",
    category: "Image post-production",
    title: "Photo editing",
    description:
      "Retouching, color correction, clipping, and clean finishing for polished campaign and e-commerce imagery.",
    capabilities: [
      "Retouching",
      "Color correction",
      "Clipping path",
      "Finishing",
    ],
    bgColor:
      "color-mix(in srgb, var(--color-brand-coral) 32%, var(--color-brand-paper))",
    media: {
      kind: "video",
      src: "/videos/home-fashion-editorial-montage.mp4",
      alt: "High-end commercial photo editing, skin retouching, and color grading video preview",
      width: 1920,
      height: 1080,
      credit: "Studio Click House",
      objectPosition: "top",
    },
    href: "/services",
  },
  {
    id: "video-editing",
    year: "03",
    category: "Motion post-production",
    title: "Video editing",
    description:
      "Commercial edits, social cutdowns, retouching, and controlled color grading shaped for every viewing format.",
    capabilities: ["Commercial edits", "Social cutdowns", "Color grading"],
    bgColor:
      "color-mix(in srgb, var(--color-brand-green) 38%, var(--color-brand-paper))",
    media: {
      kind: "video",
      src: "/videos/video-editing-wall-featured-campaign.mp4",
      alt: "Studio commercial fashion video editing and post-production preview",
      width: 1920,
      height: 1080,
      credit: "Studio Click House",
    },
    href: "/services",
  },
  {
    id: "3d-cgi",
    year: "04",
    category: "Digital production",
    title: "3D & CGI",
    description:
      "Product modeling, material development, lighting, and photorealistic CGI rendering for commercial imagery.",
    capabilities: [
      "Product modeling",
      "Materials",
      "Lighting",
      "CGI rendering",
    ],
    bgColor: "var(--color-brand-light)",
    media: {
      kind: "video",
      src: "/videos/video-editing-wall-botanical-skincare-motion.mp4",
      alt: "Photorealistic 3D cosmetic jar CGI animation with floating gummies",
      width: 1920,
      height: 1080,
      credit: "Studio Click House",
      objectPosition: "center",
    },
    href: "/services",
  },
];
